import { Box, Button, CircularProgress, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import ErrorModal from "../../../utils/ErrorModal";
import { useNavigate } from "react-router-dom";

const AddRebid = ({ tender, data, setData, setStep, user }) => {
  const fileInputRef = useRef(null);
  const [errorModal, setErrorModal] = useState(false);
  const [latestBid, setLatestBid] = useState();
  const vendorId = JSON.parse(localStorage.getItem("vendor"));
  const navigate = useNavigate();
  const [isImporting, setIsImporting] = useState(false);

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  useEffect(() => {
    const getWorkItems = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/tender/work-items?tenderId=${tender.tenderId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } }
        );
        if (res.data.length > 0) {
          setData(res.data.map((item) => ({ ...item, rate: 0, total: 0 })));
        }
      } catch (error) {
        console.log(error);
      }
    };
    getWorkItems();
  }, []);

  useEffect(() => {
    const getLatestBid = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/bid/get-latest?vendorId=${vendorId}&tenderId=${tender.tenderId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } }
        );
        setLatestBid(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getLatestBid();
  }, []);

  const handleChange = (index, field, value) => {
    if (value.includes(".")) {
      const [intPart, decPart] = value.split(".");
      if (decPart.length > 2) {
        value = intPart + "." + decPart.slice(0, 2);
      }
    }
    const updatedData = [...data];
    updatedData[index][field] = value;
    updatedData[index].total =
      updatedData[index].quantity * updatedData[index].rate;
    setData(updatedData);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data, { cellStyles: true });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Work Items");
    XLSX.writeFile(workbook, "BidDetails.xlsx");
  };

  const importFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const bufferArray = e.target.result;
      const workbook = XLSX.read(bufferArray, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet);

      const normalize = (val) => (val || "").toString().replace(/\s+/g, " ").trim().toLowerCase();

      const updatedData = data.map((row) => {
        const matchingRow = parsedData.find(
          (excelRow) =>
            excelRow.itemName === row.itemName &&
            normalize(excelRow.description) === normalize(row.description) &&
            excelRow.section === row.section &&
            excelRow.unit === row.unit &&
            excelRow.quantity === row.quantity
        );
        let rate = parseFloat(matchingRow?.rate);
        if (isNaN(rate)) rate = 0;
        return {
          ...row,
          rate: rate.toFixed(2),
          total: parseFloat(
            (row.quantity || 0) * (rate.toFixed(2) || 0)
          ).toFixed(2),
        };
      });

      setData(updatedData);
      setIsImporting(false);
      event.target.value = null;
    };
    reader.readAsArrayBuffer(file);
  };

  const grandTotal = data
    .reduce((sum, row) => sum + parseFloat(row.quantity * row.rate || 0), 0)
    .toFixed(2);

  const handleNext = async () => {
    const filteredData = data.filter((item) => {
      const isAllEmpty =
        (!item.itemName || item.itemName.toString().trim() === "") &&
        (!item.description || item.description.toString().trim() === "") &&
        (!item.section || item.section.toString().trim() === "") &&
        (!item.unit || item.unit.toString().trim() === "") &&
        (!item.quantity || Number(item.quantity) <= 0) &&
        (!item.rate || Number(item.rate) <= 0);

      return !isAllEmpty; // drop only empty rows
    });

    const hasInvalidRate = filteredData.some(
      (item) => !item.rate || Number(item.rate) <= 0
    );
    if (filteredData.length === 0 || hasInvalidRate) {
      setErrorModal(true);
      return;
    }
    setData(filteredData);
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    navigate("/vendor/dashboard");
  };

  const groupedWorkItems =
    latestBid &&
    latestBid.bidWorkItems.reduce((acc, item) => {
      if (!acc[item.section]) acc[item.section] = [];
      acc[item.section].push(item);
      return acc;
    }, {});

  return (
    <div>
      <div className="dashboard-container">
        {isImporting && (
          <div className="loading-div uploading-div">
            Uploading Sheet... <CircularProgress color="#fff" />
          </div>
        )}
        <Typography className="form-title">Your Previous Bid</Typography>
        {latestBid && (
          <Box sx={{ display: "flex", overflow: "hidden" }}>
            {/* Fixed Left Table */}
            <table
              border={1}
              style={{
                borderCollapse: "collapse",
                width: "100%",
                textAlign: "center",
                border: "1px solid #ccc",
                marginBottom: "15px",
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "20px" }}>Item Name</th>
                  <th>Item Description</th>
                  <th>UOM</th>
                  <th>Quantity</th>
                  <th>Rate (in &#8377;)</th>
                  <th>Total (in &#8377;)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedWorkItems).map(
                  ([section, items], sectionIndex) => {
                    const sectionItems = latestBid.bidWorkItems.filter((bi) =>
                      items.find((wi) => wi.itemName === bi.itemName)
                    );
                    const sectionTotal = sectionItems.reduce(
                      (sum, item) => sum + (item.quantity * item.rate || 0),
                      0
                    );

                    return (
                      <React.Fragment key={sectionIndex}>
                        {/* Section Header with Section Total */}
                        <tr
                          style={{
                            borderTop: "1px solid #ccc",
                            fontWeight: "bold",
                          }}
                        >
                          <td
                            colSpan={5}
                            style={{
                              textAlign: "left",
                              padding: "0px 10px",
                              height: "40px",
                            }}
                          >
                            Section: {section}
                          </td>
                          <td
                            style={{
                              textAlign: "end",
                              padding: "10px",
                              height: "40px",
                            }}
                          >
                            {new Intl.NumberFormat("en-IN", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(sectionTotal)}
                          </td>
                        </tr>

                        {/* Each Item Row */}
                        {items
                          .sort((a, b) => a.itemOrder - b.itemOrder)
                          .map((item, i) => (
                            <tr key={i}>
                              <td style={{ padding: "20px" }}>
                                {item.itemName}
                              </td>
                              <td>{item.description}</td>
                              <td>{item.unit}</td>
                              <td>{item.quantity}</td>
                              <td>{item.rate}</td>
                              <td>{(item.quantity * item.rate).toFixed(2)}</td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  }
                )}

                {/* Grand Total Row */}
                <tr style={{ fontWeight: "bold" }}>
                  <td colSpan={5} style={{ padding: "20px", textAlign: "end" }}>
                    Grand Total (in &#8377;):
                  </td>
                  <td
                    style={{
                      textAlign: "end",
                      padding: "10px",
                      height: "40px",
                    }}
                  >
                    {new Intl.NumberFormat("en-IN", {
                      style: "decimal",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(
                      latestBid.bidWorkItems.reduce(
                        (acc, item) => acc + (item.quantity * item.rate || 0),
                        0
                      )
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
        )}
      </div>
      <h2>Work Items Details</h2>
      <div className="dashboard-container">
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            minWidth: "100%",
            marginTop: "10px",
            textAlign: "center",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px" }}>Item Name</th>
              <th>Description</th>
              <th>Section</th>
              <th>Unit</th>
              <th>Quantity</th>
              <th style={{ width: "150px" }}>Rate (in &#8377;)</th>
              <th>Total (in &#8377;)</th>
            </tr>
          </thead>
          <tbody>
            {data
              .sort((a, b) => a.itemOrder - b.itemOrder)
              .map((row, index) => (
                <tr key={index}>
                  <td>
                    <p>{row.itemName}</p>
                  </td>
                  <td>
                    <p>{row.description}</p>
                  </td>
                  <td>
                    <p>{row.section}</p>
                  </td>
                  <td>
                    <p>{row.unit}</p>
                  </td>
                  <td>
                    <p>{row.quantity}</p>
                  </td>
                  <td>
                    <input
                      className="table-input"
                      type="number"
                      value={row.rate}
                      onChange={(e) =>
                        handleChange(index, "rate", e.target.value)
                      }
                      step={0.01}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <p>
                      {(
                        parseFloat(row.rate || 0) *
                        parseFloat(row.quantity || 0)
                      ).toFixed(2)}
                    </p>
                  </td>
                </tr>
              ))}

            <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
              <td colSpan={6} style={{ textAlign: "right", padding: "20px" }}>
                Grand Total (in &#8377;):
              </td>
              <td style={{ width: "150px", textAlign: "center" }}>
                {grandTotal}
              </td>
            </tr>
          </tbody>
        </table>
        <Typography variant="p" fontWeight={700} fontSize={"14px"}>
          [Important Note: <br />
          - Uploading Sheet will overwrite existing work items. <br />-
          Modifying any data other than rate column may cause errors while
          uploading. ]
        </Typography>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "end",
          }}
        >
          <Button className="btn-outlined" onClick={handleFileSelect}>
            Upload Excel
          </Button>
          <button
            onClick={exportToExcel}
            style={{
              cursor: "pointer",
              border: "none",
              background: "none",
              borderBottom: "1px solid #000",
            }}
          >
            Download Template Excel
          </button>
        </div>
      </div>
      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={importFromExcel}
        style={{ display: "none" }}
      />
      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" onClick={handleNext}>
          Next
        </Button>
      </Box>

      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        msg={"Item rates cannot be 0."}
      />
    </div>
  );
};

export default AddRebid;
